import { useCallback, useEffect, useMemo, useState } from "react";
import classes from "./bulkSpareDetailPage.module.css";
import { useParams } from "react-router-dom";

import {
  useGetBulkSpareByIdQuery,
  useUpdateBulkSpareByIdMutation,
  useUpdateHotSellingStatusMutation,
} from "../../../services/bulk/spares/bulkSparesApiSlice";

import { BulkSpareDetailForm } from "../../../component/bulk/bulkSpares/bulkSparesDetail/BulkSpareDetailForm";
import { BulkSpareGalleryPage } from "./bulkSpareGallery/BulkSpareGalleryPage";
import { BulkSpareDetailHead } from "../../../component/bulk/bulkSpares/bulkSparesDetail/BulkSpareDetailHead";
import { toast } from "react-toastify";
import { CategoryPageSkeleton } from "../../../component/skeleton/CategoryPageSkeleton";

export const BulkSpareDetailPage = () => {
  const { request_id } = useParams();

  const [isHotSelling, setIsHotSelling] = useState(false);

  const { data: bulkSpareData, isSuccess: isBulkSpareDataSuccess } =
    useGetBulkSpareByIdQuery(request_id);

  const [updateBulkSpare, { isLoading }] = useUpdateBulkSpareByIdMutation();
  const [updateBulkSPareLot] = useUpdateHotSellingStatusMutation();

  const initialValues = useMemo(() => {
    if (!bulkSpareData) return null;
    return { ...bulkSpareData.data };
  }, [bulkSpareData]);

  useEffect(() => {
    if (initialValues) {
      setIsHotSelling(initialValues.is_hot_selling === 1);
    }
  }, [initialValues]);

  const toggleHotSelling = useCallback(async () => {
    if (!request_id) return;

    const newStatus = !isHotSelling;

    try {
      await updateBulkSPareLot({ request_id }).unwrap();

      setIsHotSelling(newStatus);

      toast.success("Hot selling status updated!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update hot selling status.");
    }
  }, [isHotSelling, request_id, updateBulkSPareLot]);

  const handleSubmit = useCallback(
    async (formData) => {
      const payload = { ...formData };

      const toastId = toast.loading("Updating bulk spare...");

      try {
        await updateBulkSpare({
          request_id,
          data: payload,
        }).unwrap();

        toast.update(toastId, {
          render: "Bulk spare updated successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } catch (error) {
        const errorMessage =
          error?.data?.message ||
          error?.data?.error ||
          "Something went wrong. Please try again.";

        toast.update(toastId, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
    },
    [request_id, updateBulkSpare],
  );

  if (!initialValues) return <CategoryPageSkeleton />;

  return isBulkSpareDataSuccess ? (
    <div className={classes.wrapper}>
      <BulkSpareDetailHead
        lot_name={initialValues?.lot_name}
        uploaded_on={initialValues?.uploaded_on}
        approval_status={initialValues?.approval_status}
        isHotSelling={isHotSelling}
        onChange={toggleHotSelling}
      />

      <div className={classes.box}>
        <div className={classes.box__section}>
          <BulkSpareDetailForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            isSubmitting={isLoading}
          />
        </div>

        <div className={classes.box__section}>
          <BulkSpareGalleryPage />
        </div>
      </div>
    </div>
  ) : (
    <CategoryPageSkeleton />
  );
};
